import { CrudStoreItemOnboarding } from 'legacy/lut/classes/models/onboarding/CrudStoreItemOnboarding'

import WebSocketStore from 'core/classes/services/WebSocketStore'
import { CrudStore } from 'core/types/crudStore'
import { IException } from 'onboarding/components/widgets/public/admin/exceptionsReasons/models'
import {
  IInvalidParamsErrorResponsePayload,
  ILinkedSpecializationErrorResponsePayload,
  IResponseErrorPayload,
} from 'onboarding/types/response'
import {
  throwOnErrorWithoutPayload,
  throwOnInvalidRequestParams,
  throwOnLinkedSpecializationError,
} from 'onboarding/utils/errorResponse/throwOnApiError'

export type TCreateExceptionPayload = {
  Code: string
  Type: string
  Domain: string
  Group: string | null
  IsCycleEffect: number
  IsMultiSelect: number
  Active: number
}

export type TUpdateExceptionPayload = {
  Code: string
  Domain: string
  Group: string | null
  IsCycleEffect: number
  IsMultiSelect: number
  Active: number
}

const CONTROLLER = 'exception'

class ExceptionApi {
  public getExceptionsRequestsListCrud(cacheKey: string) {
    return CrudStore.get(CrudStoreItemOnboarding.ExceptionsRequestsList, cacheKey)
  }

  public getListGroupsCrud() {
    return CrudStore.get(CrudStoreItemOnboarding.ExceptionGroups)
  }

  public clearCacheListGroupsCrud(needTriggerSubscribers?: boolean) {
    this.getListGroupsCrud().removeQueries(needTriggerSubscribers)
  }

  public async listGroups() {
    const response = await WebSocketStore.get('onboarding').call<{ items: string[] }>({
      service: CONTROLLER,
      action: 'list-groups',
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to list exception groups')
    }

    return response.data.items
  }

  public async create(payload: TCreateExceptionPayload) {
    const response = await WebSocketStore.get('onboarding').call<{
      item: IException
      errorPayload?: IInvalidParamsErrorResponsePayload | IResponseErrorPayload
    }>({
      service: CONTROLLER,
      action: 'create',
      payload,
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      const fallbackMessage = 'Failed to create exception'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return response.data.item
  }

  public async update(
    code: string,
    type: string,
    payload: TUpdateExceptionPayload
  ): Promise<IException> {
    const response = await WebSocketStore.get('onboarding').call<{
      item: IException
      errorPayload?:
        | IInvalidParamsErrorResponsePayload
        | ILinkedSpecializationErrorResponsePayload
        | IResponseErrorPayload
    }>({
      service: CONTROLLER,
      action: 'update',
      payload: { code, type, payload },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      const fallbackMessage = 'Failed to update exception'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnLinkedSpecializationError(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return response.data.item
  }

  public async deleteOne(code: string, type: string): Promise<boolean> {
    const response = await WebSocketStore.get('onboarding').call<{
      deleted: boolean
      errorPayload?:
        | IInvalidParamsErrorResponsePayload
        | ILinkedSpecializationErrorResponsePayload
        | IResponseErrorPayload
    }>({
      service: CONTROLLER,
      action: 'deleteOne',
      payload: { code, type },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      const fallbackMessage = 'Failed to delete exception'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnLinkedSpecializationError(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return response.data.deleted
  }
}

export const exceptionApi = new ExceptionApi()
